/* eslint-disable no-param-reassign, max-lines */
import { api } from "jiffy-api";
import ResendTimerHandler from "./timerUtils";
import AddressSaveHandler from "./addressSaveUtils";

const VERIFICATION_CODE_LENGTH = 6;
const DEFAULT_ACTION_TYPE = "verify_address";
const MODAL_ERROR_MESSAGES = [
  "Too many failed attempts. Please try again after 30 minutes.",
  "Invalid verification code. Please try again.",
  "Please enter a 6-digit code"
];

/**
 * Handler class for two-factor authentication operations
 * Manages verification code sending, validation, and modal interactions
 */
class TwoFactorAuthHandler {
  /**
   * Creates a new TwoFactorAuthHandler instance
   * @param {Object} context - The context object containing required dependencies
   * @param {Object} context.store - Vuex store instance for state management
   * @param {Function} context.saveAddressFn - Function to save address data
   * @param {Function} context.selectNewlyCreatedAddressFn - Function to select newly created address
   * @param {Function} context.resetFormFn - Function to reset form state
   * @param {Function} context.emitFn - Function to emit events to parent component
   * @param {Object} context.isSubmittingRef - Ref object for tracking submission state
   * @param {Object} context.errorMessageRef - Ref object for error messages
   */
  constructor(context) {
    this.store = context.store;
    this.saveAddress = context.saveAddressFn;
    this.selectNewlyCreatedAddressFn = context.selectNewlyCreatedAddressFn;
    this.resetFormFn = context.resetFormFn;
    this.emitFn = context.emitFn;
    this.isSubmittingRef = context.isSubmittingRef;
    this.errorMessageRef = context.errorMessageRef;
  }

  /**
   * Resets the modal form to initial state
   * Clears input values, hides errors, and resets button states
   * @param {HTMLElement} modal - The modal element to reset
   */
  static resetModalForm(modal) {
    const elements = {
      codeInput: modal.querySelector("#account-verification-code-input"),
      errorDiv: modal.querySelector("#verification-error"),
      resendBtn: modal.querySelector("#resend-code-btn"),
      verifyBtn: modal.querySelector("#verify-code-btn"),
      timerSpan: modal.querySelector("#resend-timer"),
      timerContainer: modal.querySelector("#resend-timer-container"),
      contactSupport: modal.querySelector("#contact-support-section")
    };

    if (elements.codeInput) elements.codeInput.value = "";
    if (elements.errorDiv) elements.errorDiv.style.display = "none";
    if (elements.resendBtn) {
      elements.resendBtn.disabled = true;
      elements.resendBtn.style.display = "none";
      elements.resendBtn.innerHTML = '<i class="fa fa-redo"></i> Resend';
    }
    if (elements.timerSpan) elements.timerSpan.textContent = "30";
    if (elements.timerContainer) elements.timerContainer.style.display = "block";
    if (elements.contactSupport) elements.contactSupport.style.display = "none";
    if (elements.verifyBtn) {
      elements.verifyBtn.disabled = false;
      elements.verifyBtn.innerHTML = "<span>Verify</span>";
    }
  }

  /**
   * Clears the modal timer and resets the form
   * @param {HTMLElement} modal - The modal element
   * @private
   */
  static clearModalTimer(modal) {
    if (modal.resendTimer) {
      clearInterval(modal.resendTimer);
      modal.resendTimer = null;
    }
    TwoFactorAuthHandler.resetModalForm(modal);
  }

  /**
   * Hides the modal element using appropriate modal library
   * Supports Bootstrap 5, Bootstrap 4/jQuery, and fallback methods
   * @param {HTMLElement} modal - The modal element to hide
   * @private
   */
  static hideModalElement(modal) {
    if (window.bootstrap && window.bootstrap.Modal) {
      const bsModal = window.bootstrap.Modal.getInstance(modal);
      if (bsModal) bsModal.hide();
    } else if (window.$ && window.$.fn.modal) {
      window.$(modal).modal("hide");
    } else {
      modal.style.display = "none";
      modal.classList.remove("show");
      const backdrop = document.querySelector(".modal-backdrop");
      if (backdrop) backdrop.remove();
      document.body.classList.remove("modal-open");
    }
  }

  /**
   * Hides the 2FA modal by clearing timers, resetting form, and hiding the modal
   * @param {HTMLElement} modal - The modal element to hide
   */
  hideModal(modal) {
    try {
      TwoFactorAuthHandler.clearModalTimer(modal);
      TwoFactorAuthHandler.hideModalElement(modal);
    } catch {
      modal.style.display = "none";
    } finally {
      this.isSubmittingRef.value = false;
    }
  }

  /**
   * Creates FormData for verification API requests
   * @param {string|null} code - The verification code (optional)
   * @param {string} actionType - The type of action being verified
   * @returns {FormData} The constructed form data
   * @private
   */
  static createVerificationFormData(code = null, actionType = DEFAULT_ACTION_TYPE) {
    const formData = new FormData();
    if (code) formData.append("verification_code", code);
    formData.append("channel_type", "email");
    formData.append("action_type", actionType);
    formData.append(
      "authenticity_token",
      document.querySelector('meta[name="csrf-token"]')?.content || ""
    );
    return formData;
  }

  /**
   * Sends a verification code via email
   * Updates UI to show loading state and handles success/error responses
   * @param {HTMLElement} modal - The modal element
   * @returns {Promise<void>}
   */
  static async sendVerificationCode(modal) {
    const elements = {
      resendBtn: modal.querySelector("#resend-code-btn"),
      errorDiv: modal.querySelector("#verification-error"),
      errorText: modal.querySelector(".error-text")
    };

    ResendTimerHandler.setResendButtonLoading(elements.resendBtn, elements.errorDiv);

    try {
      const verificationFormData = TwoFactorAuthHandler.createVerificationFormData();
      const response = await api.account.twoFactorAuth.sendVerification(verificationFormData, {
        raw: true,
        headers: { "Content-Type": null }
      });

      if (response.success) {
        ResendTimerHandler.startResendTimer(modal);
      } else {
        throw new Error(response.error || "Failed to send verification code");
      }
    } catch (error) {
      ResendTimerHandler.restoreResendButton(elements, error.message);
    }
  }

  /**
   * Handles successful verification by saving address and updating store
   * @param {HTMLElement} modal - The modal element
   * @param {string} authToken - The authentication token from successful verification
   * @returns {Promise<void>}
   * @private
   */
  async handleVerificationSuccess(modal, authToken) {
    this.hideModal(modal);
    TwoFactorAuthHandler.resetModalForm(modal);

    const addressSaveHandler = new AddressSaveHandler({
      saveAddressFn: this.saveAddress,
      selectNewlyCreatedAddressFn: this.selectNewlyCreatedAddressFn,
      resetFormFn: this.resetFormFn,
      emitFn: this.emitFn,
      isSubmittingRef: this.isSubmittingRef,
      store: this.store
    });
    await addressSaveHandler.execute(authToken);
  }

  /**
   * Handles verification error by updating UI with error message
   * @param {Error} error - The error object
   * @param {Object} elements - DOM elements to update
   * @param {HTMLElement} elements.errorText - Error text element
   * @param {HTMLElement} elements.errorDiv - Error container element
   * @param {HTMLElement} elements.verifyBtn - Verify button element
   * @param {HTMLElement} elements.codeInput - Code input element
   * @private
   */
  static handleVerificationError(error, elements) {
    const verificationError =
      error.message === "Too many failed attempts. Please try again after 30 minutes."
        ? error.message
        : error.message || "Invalid verification code. Please try again.";

    if (elements.errorText) {
      elements.errorText.textContent = verificationError;
    }
    if (elements.errorDiv) {
      elements.errorDiv.style.display = "block";
    }
    if (elements.verifyBtn) {
      elements.verifyBtn.disabled = false;

      elements.verifyBtn.innerHTML = "<span>Verify</span>";
    }
    if (elements.codeInput) elements.codeInput.focus();
  }

  /**
   * Determines if error should be set on component level
   * Returns false for modal-specific errors that should only show in modal
   * @param {Error} error - The error object
   * @returns {boolean} True if error should be set on component
   * @private
   */
  static shouldSetComponentError(error) {
    return !MODAL_ERROR_MESSAGES.includes(error.message);
  }

  /**
   * Handles verification code submission
   * Validates code length, submits to API, and processes response
   * @param {HTMLElement} modal - The modal element
   * @param {string} code - The verification code entered by user
   * @returns {Promise<void>}
   */
  async handleVerification(modal, code) {
    const elements = {
      verifyBtn: modal.querySelector("#verify-code-btn"),
      errorDiv: modal.querySelector("#verification-error"),
      errorText: modal.querySelector(".error-text"),
      codeInput: modal.querySelector("#account-verification-code-input")
    };

    if (code.length !== VERIFICATION_CODE_LENGTH) {
      if (elements.errorText) elements.errorText.textContent = "Please enter a 6-digit code";
      if (elements.errorDiv) elements.errorDiv.style.display = "block";
      return;
    }

    if (elements.verifyBtn) {
      elements.verifyBtn.disabled = true;
      elements.verifyBtn.innerHTML = '<i class="fa fa-spinner fa-spin"></i> Verifying...';
    }
    if (elements.errorDiv) elements.errorDiv.style.display = "none";

    try {
      const verificationFormData = TwoFactorAuthHandler.createVerificationFormData(code);
      const result = await api.account.twoFactorAuth.verifyAccountChanges(verificationFormData, {
        raw: true,
        headers: { "Content-Type": null }
      });

      if (result.success) {
        await this.handleVerificationSuccess(modal, result.auth_token);
      } else {
        throw new Error(result.error || "Invalid verification code");
      }
    } catch (error) {
      if (this.errorMessageRef && TwoFactorAuthHandler.shouldSetComponentError(error)) {
        this.errorMessageRef.value = error.message || "Verification failed";
      }
      TwoFactorAuthHandler.handleVerificationError(error, elements);
    }
  }
}

export default TwoFactorAuthHandler;
